"use client";
import { twMerge } from "tailwind-merge";
import type { LinkComponentType } from "../../index";
import { formatPhoneNumber } from "../../utils/formatting/phone";
import { Avatar } from "../Avatar";
import { Card } from "../Card";
import { Icon } from "../Icon";
import { Skeleton } from "../Skeleton";
import { TextLink } from "../TextLink";

export interface ContactCardProps {
  /** First name */
  firstName: string;

  /** Last name */
  lastName: string;

  /** Email address (optional) */
  email?: string;

  /** Phone number (optional) */
  phone?: string;

  /** Avatar image URL (optional) */
  avatarUrl?: string;

  /** Link to contact detail page (optional) */
  href?: string;

  /** Custom link component (e.g., Next.js Link) */
  LinkComponent?: LinkComponentType;

  /** Card variant */
  variant?: "outlined" | "elevated" | "filled" | "ghost";

  /** Layout orientation
   * @default "horizontal"
   * - "horizontal": Avatar on left, info on right
   * - "vertical": Avatar on top, info below (centered, better for narrow/square containers)
   */
  layout?: "horizontal" | "vertical";

  /** Show email address
   * @default true
   */
  showEmail?: boolean;

  /** Show phone number
   * @default false
   */
  showPhone?: boolean;

  /** Loading state */
  isLoading?: boolean;

  /** Additional classes for the card */
  className?: string;

  /** Additional classes for the card content (for padding control, etc.) */
  contentClassName?: string;

  /**
   * Entity identifier for PII masking (e.g., "contact:ctc_abc123")
   * When provided, adds data-pii-* attributes for the Curtain extension
   */
  piiEntity?: string;
}

/**
 * ContactCard
 *
 * A card component for displaying contact information with avatar.
 * Includes name, optional email, and optional phone number.
 *
 * @example
 * ```tsx
 * <ContactCard
 *   firstName="John"
 *   lastName="Doe"
 *   email="john.doe@example.com"
 *   phone="+1 (555) 123-4567"
 *   showEmail
 *   showPhone
 *   href="/contacts/123"
 * />
 * ```
 */
export function ContactCard({
  firstName,
  lastName,
  email,
  phone,
  avatarUrl,
  href,
  LinkComponent,
  variant = "outlined",
  layout = "horizontal",
  showEmail = true,
  showPhone = false,
  isLoading = false,
  className,
  contentClassName,
  piiEntity,
}: ContactCardProps) {
  const fullName = `${firstName} ${lastName}`.trim();

  // Link wrapper component
  const LinkWrapper = ({ children, className: linkClassName }: { children: React.ReactNode; className?: string }) => {
    if (!href) {
      return <span className={linkClassName}>{children}</span>;
    }

    if (LinkComponent) {
      return (
        <LinkComponent href={href} className={linkClassName}>
          {children}
        </LinkComponent>
      );
    }

    return (
      <TextLink href={href} variant="unstyled" className={twMerge("hover:underline", linkClassName)} asButton={false}>
        {children}
      </TextLink>
    );
  };

  const isVertical = layout === "vertical";

  if (isLoading) {
    return (
      <Card variant={variant} className={twMerge("flex flex-col", className)}>
        <Card.Content className="flex items-center flex-1">
          <div
            className={twMerge("w-full", isVertical ? "flex flex-col items-center gap-4" : "flex items-center gap-4")}
          >
            <Skeleton width={56} height={56} className="rounded flex-shrink-0" />
            <div className={twMerge(isVertical ? "w-full text-center" : "flex-1", "space-y-2")}>
              <Skeleton width="70%" height={20} className={twMerge(isVertical && "mx-auto")} />
              <Skeleton width="100%" height={16} className={twMerge(isVertical && "mx-auto")} />
            </div>
          </div>
        </Card.Content>
      </Card>
    );
  }

  return (
    <Card variant={variant} className={twMerge("group/card flex flex-col", className)}>
      <Card.Content className={twMerge("flex items-center flex-1", href && "cursor-pointer", contentClassName)}>
        <div
          className={twMerge(
            "relative w-full",
            isVertical ? "flex flex-col items-center gap-4 text-center" : "flex items-center gap-4"
          )}
        >
          <Avatar
            firstName={firstName}
            lastName={lastName}
            src={avatarUrl}
            size="xl"
            variant="user"
            shape="square"
            className="flex-shrink-0"
            piiEntity={piiEntity}
          />
          <div className={twMerge(isVertical ? "w-full" : "flex-1 min-w-0")}>
            {href && (
              <LinkWrapper className="after:absolute after:inset-0">
                <span className="sr-only">{fullName}</span>
              </LinkWrapper>
            )}
            <span
              className="text-lg font-semibold leading-tight text-text-heading block mb-1.5 group-hover/card:text-text-brand transition-colors"
              data-pii-type={piiEntity ? "full-name" : undefined}
              data-pii-entity={piiEntity}
            >
              {fullName}
            </span>
            <div className={twMerge("space-y-0.5", isVertical && "flex flex-col items-center")}>
              {showEmail && email && (
                <div className="flex items-center gap-2.5 text-text-body">
                  <Icon name="Envelope" size={16} className="text-text-muted flex-shrink-0" />
                  <TextLink
                    href={`mailto:${email}`}
                    variant="unstyled"
                    className={twMerge(
                      "text-sm leading-relaxed text-text-body relative z-20",
                      isVertical ? "" : "truncate"
                    )}
                    data-pii-type={piiEntity ? "email" : undefined}
                    data-pii-entity={piiEntity}
                  >
                    {email}
                  </TextLink>
                </div>
              )}
              {showPhone && phone && (
                <div className="flex items-center gap-2.5 text-text-body">
                  <Icon name="Phone" size={16} className="text-text-muted flex-shrink-0" />
                  <TextLink
                    href={`tel:${phone}`}
                    variant="unstyled"
                    className={twMerge(
                      "text-sm leading-relaxed text-text-body relative z-20",
                      isVertical ? "" : "truncate"
                    )}
                    data-pii-type={piiEntity ? "phone" : undefined}
                    data-pii-entity={piiEntity}
                  >
                    {formatPhoneNumber(phone)}
                  </TextLink>
                </div>
              )}
            </div>
          </div>
        </div>
      </Card.Content>
    </Card>
  );
}
